/**
 * Sprint M5.39 (30.07.2026) — Block A2.
 *
 * Free-Fokustypen-Bibliothek fuer das Free-Cockpit.
 *
 * - Ermittelter Primaerfokustyp: Vollinhalt (5 Erkennungsmerkmale, Positionierung).
 * - Andere 3 Fokustypen: Preview (Kurz-Beschreibung + Conversion-Hinweis „Mehr Tiefe mit Pro Solo").
 *
 * 4er-Matrix-Reihenfolge verbindlich:
 *   Oben links: Katze  · Oben rechts: Eule
 *   Unten links: Hund  · Unten rechts: Biene
 *
 * Sprint M5.45 (31.07.2026) — Robustness-Pass:
 *   - Slug-Normalisierung (case-insensitive, englische Aliase) → verhindert,
 *     dass ein Slug-Mismatch (z.B. „Katze" vs „katze" vs „cat") eine Karte
 *     unbeabsichtigt in den Primary-Slot verschiebt oder ganz aus dem Grid faellt.
 *   - Defensive Fallbacks fuer Glyph, Name, Kurzbeschreibung — falls jemals
 *     ein Fokustyp-Slug ohne Content-Eintrag durchkommt, rendern wir einen
 *     klaren Fallback-Text statt einer leeren Karte.
 *   - Alle vier Karten sind unabhaengig vom Primaerfokustyp garantiert im DOM.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { pickProSoloCopy } from "@/lib/proTrialCopy";
import {
  EuleGlyph, BieneGlyph, KatzeGlyph, HundGlyph,
} from "./FokustypGlyphs";
import { FOKUSTYP_PROFILES } from "@/lib/fokustypProfiles";

const GLYPH = { eule: EuleGlyph, biene: BieneGlyph, katze: KatzeGlyph, hund: HundGlyph };
const NAMES = { eule: "Eule", biene: "Biene", katze: "Katze", hund: "Hund" };

// 4er-Matrix-Reihenfolge (Katze OL / Eule OR / Hund UL / Biene UR)
const MATRIX_ORDER = ["katze", "eule", "biene", "hund"];

// Sprint M5.46 (31.07.2026) — Mapping deutscher UI-Slugs → englische Keys in
// FOKUSTYP_PROFILES (`lib/fokustypProfiles.js`, aus dem Pionier-Kontext).
// Damit koennen wir den Pionier-Kurzsteckbrief 1:1 wiederverwenden, ohne
// Content zu duplizieren.
const SLUG_DE_TO_EN = { katze: "cat", eule: "owl", biene: "bee", hund: "dog" };

function getKurzsteckbrief(slug) {
  const enKey = SLUG_DE_TO_EN[slug];
  return enKey ? FOKUSTYP_PROFILES[enKey] : null;
}

// Sprint M5.45 — Slug-Alias-Map. Wenn irgendwo im System der Fokustyp
// abweichend geliefert wird (Grossbuchstaben, Englisch, Legacy-Kuerzel),
// wird er hier auf den kanonischen deutschen Slug gemappt.
const SLUG_ALIAS = {
  cat: "katze",
  owl: "eule",
  bee: "biene",
  dog: "hund",
  katze: "katze",
  eule: "eule",
  biene: "biene",
  hund: "hund",
};

function normalizeSlug(slug) {
  if (!slug) return "";
  return SLUG_ALIAS[String(slug).toLowerCase().trim()] || "";
}

// Kurz-Beschreibungen fuer Preview (nicht ermittelter Fokustyp).
const SHORT_DESC = {
  eule: "Sucht Sinn, Logik und das große Bild. Beobachtet erst, dann entscheidet.",
  biene: "Denkt in Schritten und Systemen. Braucht Klarheit, wie etwas ablaufen soll.",
  katze: "Sucht Freiheit und Eigenwillen. Entscheidet lieber spontan als vorher festgelegt.",
  hund: "Sucht Beziehung und Sicherheit. Spürt Stimmungen, bevor sie ausgesprochen sind.",
};

// Sprint M5.45 — Klare Fallback-Copy, falls Content jemals fehlt (Robustheit,
// nicht als Regelzustand). Verhindert leere Preview-Karten.
const FALLBACK_SHORT = "Kurzprofil folgt in Kürze — schau später wieder rein.";

// 5 Erkennungsmerkmale je Fokustyp (Vollinhalt fuer ermittelten Primaerfokustyp).
const ERKENNUNGSMERKMALE = {
  eule: [
    "Fragt nach dem WARUM, bevor sie entscheidet.",
    "Beobachtet erst, reagiert dann.",
    "Erkennt Muster und Widersprüche, bevor andere sie bemerken.",
    "Verteidigt Positionen mit Argumenten und Daten.",
    "Wird ungeduldig, wenn Logik ignoriert wird.",
  ],
  biene: [
    "Denkt in Schritten und Reihenfolgen.",
    "Braucht einen klaren Ablauf, bevor sie startet.",
    "Erklärt komplexe Dinge so, dass andere sie verstehen.",
    "Sorgt dafür, dass nichts vergessen wird.",
    "Fühlt sich unwohl, wenn jemand ohne Plan loslegt.",
  ],
  katze: [
    "Spürt sofort, wenn jemand sie in eine Schublade stecken will.",
    "Entscheidet lieber spontan als vorher festgelegt.",
    "Sagt offen, was sie denkt — auch wenn es unbequem ist.",
    "Braucht Wahlmöglichkeiten, keine Anweisungen.",
    "Empfindet zu viele Regeln als Käfig.",
  ],
  hund: [
    "Spürt Stimmungen und Spannungen, bevor sie ausgesprochen sind.",
    "Misst Erfolg daran, ob die Menschen um sie herum okay sind.",
    "Gibt anderen Sicherheit durch Verlässlichkeit.",
    "Lässt Konflikte nicht eskalieren.",
    "Nimmt Gefühle anderer manchmal zu sehr auf sich.",
  ],
};

// Positionierung (kurze Charakterisierung nach 4er-Matrix).
// Sprint V14 Fix 4 (Boardroom-CoS Sa 05.09.2026-19 Uhr) — Technobabels raus:
// "Intrinsisch/Sachlich" ersetzt durch alltagssprachliche Positionierungen.
const POSITIONIERUNG = {
  eule: "Denkt sachlich — der Beobachter mit dem großen Bild.",
  biene: "Braucht Klarheit — Methode, Ordnung, Präzision.",
  katze: "Folgt der eigenen Lust — die Eigenwillige.",
  hund: "Fühlt die Beziehung zuerst — der Beziehungs-Anker.",
};

export default function FreeFokustypBibliothek({ primaryFokustyp, onUpgradeClick }) {
  const { user } = useAuth() || {};
  const copy = pickProSoloCopy(user);
  // Sprint M5.45 — Robustness-Pass: normalisiere den incoming Primary-Slug.
  // Sollte je ein englischer oder gemischter Slug reinkommen, wird er hier
  // sauber auf den kanonischen Slug ("katze", "eule", ...) gemappt.
  const primary = normalizeSlug(primaryFokustyp);
  return (
    <section
      data-testid="free-fokustyp-bibliothek"
      data-primary-fokustyp={primary || "unknown"}
      className="mt-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-8"
    >
      <div className="mb-5">
        <p className="text-xs font-bold uppercase tracking-widest text-[#4563BB] dark:text-[var(--fts-primary-bright)]">
          Deine Fokustypen-Bibliothek
        </p>
        <h2 className="mt-1 text-lg sm:text-xl font-extrabold text-slate-900 dark:text-white">
          Deine vier Fokustypen im Überblick
        </h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          Dein Primärfokustyp mit allem, was ihn ausmacht — und ein erster Blick auf die anderen drei.
        </p>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
        data-testid="free-fokustyp-bibliothek-grid"
      >
        {MATRIX_ORDER.map((slug) => {
          const Glyph = GLYPH[slug];
          const isPrimary = slug === primary;
          const displayName = NAMES[slug] || slug;
          const shortDesc = SHORT_DESC[slug] || FALLBACK_SHORT;
          // Sprint M5.46 (31.07.2026) — Kurzsteckbrief aus dem Pionier-Kontext.
          // Enthaelt tagline + 3 erkennungsmerkmale + staerke + schatten_seite.
          const kurz = !isPrimary ? getKurzsteckbrief(slug) : null;
          return (
            <div
              key={slug}
              data-testid={`fokustyp-card-${slug}`}
              data-role={isPrimary ? "primary" : "preview"}
              data-fokustyp={slug}
              className={`rounded-xl border p-4 ${
                isPrimary
                  ? "border-[#4563BB] bg-blue-50/40 dark:bg-[#1A2440] dark:border-[#7A92D4] shadow-sm"
                  : "border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/40"
              }`}
            >
              <div className="flex items-center gap-3">
                {Glyph ? <Glyph size={44} /> : (
                  <div
                    className="w-11 h-11 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-500 dark:text-slate-400 font-bold"
                    data-testid={`fokustyp-card-${slug}-glyph-fallback`}
                  >
                    {displayName.slice(0, 1)}
                  </div>
                )}
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                    {isPrimary ? "Dein Primärfokustyp" : "Vorschau"}
                  </p>
                  <h3
                    className="text-lg font-extrabold text-slate-900 dark:text-white"
                    data-testid={`fokustyp-card-${slug}-name`}
                  >
                    {displayName}
                  </h3>
                </div>
              </div>

              {isPrimary ? (
                <>
                  <p
                    className="mt-3 text-xs font-semibold text-[#4563BB] dark:text-[#9DB0E4]"
                    data-testid={`fokustyp-card-${slug}-positionierung`}
                  >
                    {POSITIONIERUNG[slug]}
                  </p>
                  <ul
                    className="mt-3 space-y-1.5 text-sm text-slate-700 dark:text-slate-200"
                    data-testid={`fokustyp-card-${slug}-erkennungsmerkmale`}
                  >
                    {ERKENNUNGSMERKMALE[slug].map((m, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-[#4563BB] dark:text-[#9DB0E4] font-bold flex-shrink-0">•</span>
                        <span>{m}</span>
                      </li>
                    ))}
                  </ul>
                  {/* Sprint M5.44 (31.07.2026) — Block B3:
                      Primärfokustyp bekommt einen expliziten Link zum
                      vollständigen Steckbrief unter /dashboard/fokustyp/:slug.
                      Die 5-Punkte-Liste ist damit nur noch die Kurzform,
                      der volle Steckbrief ist einen Klick entfernt. */}
                  <Link
                    to={`/dashboard/fokustyp/${slug}`}
                    className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-[#4563BB] hover:bg-[#3a54a2] px-4 py-2 text-xs font-semibold text-white transition-colors"
                    data-testid={`fokustyp-card-${slug}-full-steckbrief-cta`}
                  >
                    Vollständigen Steckbrief öffnen →
                  </Link>
                  {/* Sprint M5.40 (30.07.2026) — Block A3:
                      Auch beim eigenen Fokustyp einen dezenten Hinweis auf
                      mehr Profil-Tiefe mit Pro Solo. */}
                  <div
                    className="mt-4 pt-3 border-t border-blue-200"
                    data-testid={`fokustyp-card-${slug}-profil-tiefe`}
                  >
                    <p className="text-xs font-bold text-[#4563BB]">
                      Mehr Profil-Tiefe mit Pro Solo
                    </p>
                    <p className="mt-1 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                      In Pro Solo lernst du dein Profil noch präziser kennen —
                      und erkennst Muster über all deine Flexpfade hinweg.
                    </p>
                    <button
                      type="button"
                      onClick={onUpgradeClick}
                      data-testid={`fokustyp-card-${slug}-profil-tiefe-cta`}
                      className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#4563BB] hover:underline"
                    >
                      {copy.ctaLabelArrow}
                    </button>
                  </div>
                </>
              ) : (
                <div data-testid={`fokustyp-card-${slug}-kurzsteckbrief`}>
                  {kurz ? (
                    <>
                      {/* Sprint M5.46 (31.07.2026) — Kurzsteckbrief wie im
                          Pionier-Kontext: Tagline + 3 Erkennungsmerkmale +
                          Staerke/Schatten-Kaesten. */}
                      <p
                        className="mt-3 text-sm font-semibold text-[#4563BB] leading-snug"
                        data-testid={`fokustyp-card-${slug}-tagline`}
                      >
                        {kurz.tagline}
                      </p>
                      <ul
                        className="mt-3 space-y-1.5 text-sm text-slate-700 dark:text-slate-200"
                        data-testid={`fokustyp-card-${slug}-erkennungsmerkmale`}
                      >
                        {kurz.erkennungsmerkmale.map((m, i) => (
                          <li
                            key={i}
                            className="flex gap-2"
                            data-testid={`fokustyp-card-${slug}-erkennungsmerkmal-${i}`}
                          >
                            <span className="text-[#4563BB] font-bold flex-shrink-0">•</span>
                            <span>{m}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div
                          className="p-2.5 rounded-lg bg-emerald-50 border border-emerald-100"
                          data-testid={`fokustyp-card-${slug}-staerke`}
                        >
                          <p className="text-[10px] font-bold text-emerald-800 uppercase tracking-widest">
                            Stärke
                          </p>
                          <p className="mt-0.5 text-xs text-emerald-900 leading-snug">
                            {kurz.staerke}
                          </p>
                        </div>
                        <div
                          className="p-2.5 rounded-lg bg-amber-50 border border-amber-100"
                          data-testid={`fokustyp-card-${slug}-schatten`}
                        >
                          <p className="text-[10px] font-bold text-amber-800 uppercase tracking-widest">
                            Schatten-Seite
                          </p>
                          <p className="mt-0.5 text-xs text-amber-900 leading-snug">
                            {kurz.schatten_seite}
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p
                      className="mt-3 text-sm text-slate-700 dark:text-slate-200 leading-relaxed"
                      data-testid={`fokustyp-card-${slug}-short`}
                    >
                      {shortDesc}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={onUpgradeClick}
                    data-testid={`fokustyp-card-${slug}-conversion`}
                    className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#4563BB] hover:underline"
                  >
                    {copy.isTrialEligible
                      ? "Mehr Tiefe mit Pro Solo — 7 Tage für 1 € →"
                      : "Mehr Tiefe mit Pro Solo →"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p
        className="mt-5 text-xs text-slate-500 dark:text-slate-400 italic"
        data-testid="free-fokustyp-bibliothek-hint"
      >
        Lerne die anderen kennen mit Pro Solo — Erwartungen, Bedürfnisse, Kommunikationsstile.
      </p>
    </section>
  );
}
